// src/pages/OffersPage.js
import React, { useState, useEffect } from 'react';
import { offersAPI, customersAPI, materialsAPI, servicesAPI } from '../services/api';
import { 
  PlusCircle, 
  Edit, 
  Trash2, 
  Check, 
  X, 
  FileText, 
  Send, 
  Package, 
  Wrench,
  DollarSign,
  Calendar,
  Users,
  Clock,
  Eye,
  CheckCircle,
  AlertCircle,
  Search,
  Filter,
  ArrowUpDown,
  Download,
  Mail,
  ChevronUp,
  ChevronDown
} from 'lucide-react';

const OffersPage = () => {
  const [offers, setOffers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [currentOffer, setCurrentOffer] = useState(null);
  const [offerItems, setOfferItems] = useState([]);
  const [showNewItemForm, setShowNewItemForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [statusDropdownId, setStatusDropdownId] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    title: '',
    valid_until: '',
    is_tax_exempt: false, // Neue Eigenschaft für MwSt.-Befreiung
    discount: 0 // Neue Eigenschaft für Rabatt
  });
  const [newItemData, setNewItemData] = useState({
    description: '',
    quantity: 1,
    unit: '',
    price_per_unit: 0,
    item_type: 'material',
    material_id: '',
    service_id: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [offersResponse, customersResponse, materialsResponse, servicesResponse] = await Promise.all([
          offersAPI.getAll(),
          customersAPI.getAll(),
          materialsAPI.getAll(),
          servicesAPI.getAll()
        ]);
        
        setOffers(offersResponse.data.offers);
        setCustomers(customersResponse.data.customers);
        setMaterials(materialsResponse.data.materials);
        setServices(servicesResponse.data.services);
        setError('');
      } catch (err) {
        setError('Fehler beim Laden der Daten');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' 
      ? e.target.checked 
      : e.target.name === 'total_amount'
        ? e.target.value.replace(',', '.')
        : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === 'quantity' || name === 'price_per_unit') {
      newValue = value.replace(',', '.');
    }

    if (name === 'material_id' && value !== '') {
      const selectedMaterial = materials.find(material => material.id === parseInt(value));
      if (selectedMaterial) {
        setNewItemData({
          ...newItemData,
          material_id: value,
          service_id: '',
          description: selectedMaterial.name,
          unit: selectedMaterial.unit,
          price_per_unit: selectedMaterial.price_per_unit,
          item_type: 'material'
        });
        return;
      }
    }

    if (name === 'service_id' && value !== '') {
      const selectedService = services.find(service => service.id === parseInt(value));
      if (selectedService) {
        setNewItemData({
          ...newItemData,
          service_id: value,
          material_id: '',
          description: selectedService.name,
          unit: 'Std.',
          price_per_hour: selectedService.rate_per_hour,
          price_per_unit: selectedService.rate_per_hour,
          item_type: 'service'
        });
        return;
      }
    }

    setNewItemData({ ...newItemData, [name]: newValue });
  };

  const calculateTotalPrice = () => {
    return offerItems.reduce((total, item) => {
      return total + (parseFloat(item.quantity) * parseFloat(item.price_per_unit));
    }, 0);
  };

  const calculateDiscountAmount = () => {
    if (!formData.discount || parseFloat(formData.discount) <= 0) {
      return 0;
    }
    const subtotal = calculateTotalPrice();
    return subtotal * (parseFloat(formData.discount) / 100);
  };

  const calculateDiscountedPrice = () => {
    const subtotal = calculateTotalPrice();
    const discountAmount = calculateDiscountAmount();
    return subtotal - discountAmount;
  };

  const calculateTaxAmount = () => {
    if (formData.is_tax_exempt) {
      return 0; // Keine MwSt. wenn befreit
    }
    const subtotal = calculateDiscountedPrice(); // Auf den reduzierten Preis anwenden
    return subtotal * 0.081; // 8.1% MwSt in der Schweiz
  };

  const calculateTotalWithTax = () => {
    return calculateDiscountedPrice() + calculateTaxAmount();
  };

  const handleAddItem = () => {
    const quantity = parseFloat(newItemData.quantity);
    const pricePerUnit = parseFloat(newItemData.price_per_unit);
    const totalPrice = quantity * pricePerUnit;

    const newItem = {
      ...newItemData,
      id: `temp-${Date.now()}`,
      total_price: totalPrice
    };

    setOfferItems([...offerItems, newItem]);
    setNewItemData({
      description: '',
      quantity: 1,
      unit: '',
      price_per_unit: 0,
      material_id: '',
      service_id: '',
      item_type: 'material'
    });
    setShowNewItemForm(false);
  };

  const handleRemoveItem = (itemId) => {
    setOfferItems(offerItems.filter(item => item.id !== itemId));
  };

  const moveItemUp = (index) => {
    if (index === 0) return; // Bereits ganz oben
    const newItems = [...offerItems];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    setOfferItems(newItems);
  };

  const moveItemDown = (index) => {
    if (index === offerItems.length - 1) return; // Bereits ganz unten
    const newItems = [...offerItems];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    setOfferItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const offerPayload = {
        ...formData,
        total_amount: calculateTotalPrice().toFixed(2), // Wir speichern den Originalpreis, der Rabatt wird separat gespeichert
        is_tax_exempt: formData.is_tax_exempt,
        discount: parseFloat(formData.discount) || 0
      };
      
      const response = await offersAPI.create(offerPayload);
      const newOfferId = response.data.offer.id;
      
      for (const item of offerItems) {
        await offersAPI.addItem(newOfferId, {
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          price_per_unit: item.price_per_unit,
          item_type: item.item_type,
          material_id: item.material_id || null,
          service_id: item.service_id || null
        });
      }
      
      const updatedOffersResponse = await offersAPI.getAll();
      setOffers(updatedOffersResponse.data.offers);
      
      setShowForm(false);
      setFormData({
        customer_id: '',
        title: '',
        valid_until: '',
        is_tax_exempt: false,
        discount: 0
      });
      setOfferItems([]);
      setError('');
    } catch (err) {
      setError('Fehler beim Erstellen der Offerte');
      console.error(err);
    }
  };

  const handleEditOffer = async (offerId) => {
    try {
      setLoading(true);
      const response = await offersAPI.getById(offerId);
      const offer = response.data.offer;
      const items = response.data.items;

      setFormData({
        customer_id: offer.customer_id,
        title: offer.title,
        valid_until: offer.valid_until ? offer.valid_until.split('T')[0] : '',
        is_tax_exempt: offer.is_tax_exempt || false, // MwSt.-Befreiung aus der DB laden
        discount: offer.discount || 0
      });
      setOfferItems(items);
      setCurrentOffer(offer);
      setShowForm(true);
      setLoading(false);
    } catch (err) {
      setError('Fehler beim Laden der Offerte');
      console.error(err);
      setLoading(false);
    }
  };

  const handleUpdateOffer = async (e) => {
    e.preventDefault();
    try {
      await offersAPI.update(currentOffer.id, {
        ...formData,
        total_amount: calculateTotalPrice().toFixed(2), // Wir speichern den Originalpreis, der Rabatt wird separat gespeichert
        is_tax_exempt: formData.is_tax_exempt,
        discount: parseFloat(formData.discount) || 0
      });
      
      const response = await offersAPI.getById(currentOffer.id);
      const existingItems = response.data.items;
      
      for (const item of offerItems) {
        if (item.id.toString().startsWith('temp-')) {
          await offersAPI.addItem(currentOffer.id, {
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            price_per_unit: item.price_per_unit,
            item_type: item.item_type,
            material_id: item.material_id || null,
            service_id: item.service_id || null
          });
        } else {
          await offersAPI.updateItem(currentOffer.id, item.id, {
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            price_per_unit: item.price_per_unit,
            item_type: item.item_type,
            material_id: item.material_id || null,
            service_id: item.service_id || null
          });
        }
      }
      
      for (const existingItem of existingItems) {
        if (!offerItems.find(item => item.id === existingItem.id)) {
          await offersAPI.deleteItem(currentOffer.id, existingItem.id);
        }
      }
      
      const updatedOffersResponse = await offersAPI.getAll();
      setOffers(updatedOffersResponse.data.offers);
      
      setShowForm(false);
      setFormData({
        customer_id: '',
        title: '',
        valid_until: '',
        is_tax_exempt: false,
        discount: 0
      });
      setOfferItems([]);
      setCurrentOffer(null);
      setError('');
    } catch (err) {
      setError('Fehler beim Aktualisieren der Offerte');
      console.error(err);
    }
  };

  // PDF-Vorschau
  const handlePreviewPDF = async (offerId) => {
    try {
      setLoading(true);
      const response = await offersAPI.generatePDF(offerId);
      
      // PDF-Blob erstellen und im Browser anzeigen
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // PDF in neuem Tab öffnen
      window.open(pdfUrl, '_blank');
      setLoading(false);
    } catch (err) {
      setError('Fehler beim Generieren der PDF-Vorschau');
      console.error(err);
      setLoading(false);
    }
  };

  // PDF herunterladen
  const handleDownloadPDF = async (offerId, offerTitle) => {
    try {
      setLoading(true);
      const response = await offersAPI.generatePDF(offerId);
      
      // PDF-Blob erstellen und herunterladen
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Download-Link erstellen und simulieren
      const downloadLink = document.createElement('a');
      downloadLink.href = pdfUrl;
      downloadLink.download = `Offerte_${offerTitle.replace(/\s+/g, '_')}_${offerId}.pdf`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      setLoading(false);
    } catch (err) {
      setError('Fehler beim Herunterladen der PDF');
      console.error(err);
      setLoading(false);
    }
  };

  // PDF per E-Mail versenden
  const handleSendPDFByEmail = async (offerId) => {
    try {
      setLoading(true);
      await offersAPI.sendPDFByEmail(offerId, {
        // Hier könnten zusätzliche E-Mail-Optionen stehen, wenn nötig
      });
      
      alert('Die Offerte wurde erfolgreich per E-Mail versendet.');
      
      // Status der Offerte aktualisieren
      await offersAPI.updateStatus(offerId, 'sent');
      
      // Offertenliste aktualisieren
      const updatedOffersResponse = await offersAPI.getAll();
      setOffers(updatedOffersResponse.data.offers);
      
      setLoading(false);
    } catch (err) {
      setError('Fehler beim Versenden der PDF per E-Mail');
      console.error(err);
      setLoading(false);
    }
  };
  
  // Toggle-Funktion für das Status-Dropdown
  const toggleStatusDropdown = (offerId, e) => {
    // Event stoppen, damit es nicht bis zum Dokument propagiert
    if (e) e.stopPropagation();
    
    // Status umschalten
    setStatusDropdownId(statusDropdownId === offerId ? null : offerId);
  };
  
  // Event-Listener für Klicks außerhalb des Dropdowns
  useEffect(() => {
    // Nur hinzufügen, wenn ein Dropdown geöffnet ist
    if (statusDropdownId === null) return;
    
    const handleClickOutside = (e) => {
      const dropdown = document.querySelector('[data-dropdown="' + statusDropdownId + '"]');
      if (dropdown && !dropdown.contains(e.target)) {
        setStatusDropdownId(null);
      }
    };
    
    // Auf Dokument-Ebene registrieren
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup bei Unmount oder Änderung
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [statusDropdownId]);

  // Status einer Offerte ändern
  const handleStatusChange = async (offerId, newStatus) => {
    try {
      setLoading(true);
      
      // API-Aufruf zum Aktualisieren des Status
      await offersAPI.updateStatus(offerId, newStatus);
      
      // Offertenliste aktualisieren
      const updatedOffersResponse = await offersAPI.getAll();
      setOffers(updatedOffersResponse.data.offers);
      
      // Status-Dropdown schließen
      setStatusDropdownId(null);
      
      setLoading(false);
    } catch (err) {
      setError('Fehler beim Aktualisieren des Status');
      console.error(err);
      setLoading(false);
    }
  };

  const handleDeleteOffer = async (offerId, offerTitle) => {
    if (!window.confirm(`Möchten Sie die Offerte "${offerTitle}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }
    
    try {
      setLoading(true);
      await offersAPI.delete(offerId);
      
      // Offertenliste aktualisieren
      const updatedOffersResponse = await offersAPI.getAll();
      setOffers(updatedOffersResponse.data.offers);
      
      setLoading(false);
      setError('');
    } catch (err) {
      setError('Fehler beim Löschen der Offerte');
      console.error(err);
      setLoading(false);
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      draft: 'Entwurf',
      sent: 'Gesendet',
      accepted: 'Akzeptiert',
      rejected: 'Abgelehnt'
    };
    return statusMap[status] || status;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft':
        return <Edit size={16} className="mr-1" />;
      case 'sent':
        return <Send size={16} className="mr-1" />;
      case 'accepted':
        return <CheckCircle size={16} className="mr-1" />;
      case 'rejected':
        return <AlertCircle size={16} className="mr-1" />;
      default:
        return <FileText size={16} className="mr-1" />;
    }
  };

  const getStatusColor = (status) => {
    const colorMap = {
      draft: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      sent: 'bg-blue-100 text-blue-800 border-blue-200',
      accepted: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-CH');
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount).toFixed(2).replace('.', ',');
  };
  const filteredOffers = offers
    .filter(offer => {
      if (filterStatus === 'all') return true;
      return offer.status === filterStatus;
    })
    .filter(offer => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        offer.title.toLowerCase().includes(searchLower) ||
        (offer.company_name && offer.company_name.toLowerCase().includes(searchLower)) ||
        (offer.first_name && offer.first_name.toLowerCase().includes(searchLower)) ||
        (offer.last_name && offer.last_name.toLowerCase().includes(searchLower))
      );
    });

  if (loading && !offers.length) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-12 w-12 rounded-full bg-primary-200 mb-4"></div>
          <div className="h-4 w-32 bg-primary-100 rounded"></div>
          <div className="mt-3 text-gray-500">Lade Offertendaten...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">
      {/* Header mit Titel und Aktionen */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Offertenverwaltung</h1>
          <p className="text-sm text-gray-600 mt-1">Erstellen und verwalten Sie Ihre Kundenangebote</p>
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setCurrentOffer(null);
            if (!showForm) {
              setFormData({
                customer_id: '',
                title: '',
                valid_until: '',
                is_tax_exempt: false,
                discount: 0
              });
              setOfferItems([]);
            }
          }}
          className={`btn-modern flex items-center justify-center px-5 py-2.5 w-full sm:w-auto ${
            showForm 
              ? 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300' 
              : 'bg-primary-600 hover:bg-primary-700 text-white shadow-md-soft'
          }`}
        >
          {showForm ? (
            <>
              <X size={20} className="mr-2" /> Abbrechen
            </>
          ) : (
            <>
              <PlusCircle size={20} className="mr-2" /> Neue Offerte
            </>
          )}
        </button>
      </div>

      {/* Fehlermeldung */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg shadow-sm">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Formular für neue/bearbeitete Offerte */}
      {showForm && (
        <div className="card-modern mb-6 overflow-hidden">
          <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-primary-50 to-primary-100 border-b border-primary-200">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
              {currentOffer ? 'Offerte bearbeiten' : 'Neue Offerte erstellen'}
            </h2>
          </div>

          <form onSubmit={currentOffer ? handleUpdateOffer : handleSubmit} className="p-4 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Kunde auswählen *</label>
                <select
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleChange}
                  required
                  className="touch-target w-full px-4 py-3 border-2 border-gray-200 rounded-xl shadow-soft focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                >
                  <option value="">-- Kunden auswählen --</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.company_name ? `${customer.company_name} (${customer.first_name} ${customer.last_name})` : `${customer.first_name} ${customer.last_name}`}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gültig bis</label>
                <div className="relative">
                  <input
                    type="date"
                    name="valid_until"
                    value={formData.valid_until}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-400"
                  />
                  <Calendar className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel der Offerte *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  placeholder="z.B. Webseite-Entwicklung, Server-Hosting, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-400"
                />
              </div>
            </div>

            {/* Positionen */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-800">Offertenpositionen</h3>
                <button
                  type="button"
                  onClick={() => setShowNewItemForm(true)}
                  className="inline-flex items-center text-primary-600 hover:text-primary-800 font-medium"
                >
                  <PlusCircle size={18} className="mr-1" /> 
                  Position hinzufügen
                </button>
              </div>

              {/* Formular für neue Position */}
              {showNewItemForm && (
                <div className="bg-gray-50 p-5 rounded-xl mb-6 border border-gray-200 shadow-sm">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-medium text-gray-800">Neue Position</h4>
                    <button
                      type="button"
                      onClick={() => setShowNewItemForm(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  
                  {/* Typ-Auswahl */}
                  <div className="flex mb-5 bg-white rounded-lg border border-gray-300 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setNewItemData({...newItemData, item_type: 'material'})}
                      className={`flex-1 py-2.5 px-4 text-center flex justify-center items-center gap-2 ${
                        newItemData.item_type === 'material' 
                          ? 'bg-primary-600 text-white' 
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Package size={18} />
                      Material
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewItemData({...newItemData, item_type: 'service'})}
                      className={`flex-1 py-2.5 px-4 text-center flex justify-center items-center gap-2 ${
                        newItemData.item_type === 'service' 
                          ? 'bg-primary-600 text-white' 
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Wrench size={18} />
                      Dienstleistung
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Material oder Dienstleistung auswählen */}
                    {newItemData.item_type === 'material' ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Material auswählen</label>
                        <select
                          name="material_id"
                          value={newItemData.material_id || ''}
                          onChange={handleItemChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-400"
                        >
                          <option value="">-- Eigene Beschreibung --</option>
                          {materials.map((material) => (
                            <option key={material.id} value={material.id}>
                              {material.name} ({formatCurrency(material.price_per_unit)} CHF/{material.unit})
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Dienstleistung auswählen</label>
                        <select
                          name="service_id"
                          value={newItemData.service_id || ''}
                          onChange={handleItemChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-400"
                        >
                          <option value="">-- Eigene Beschreibung --</option>
                          {services.map((service) => (
                            <option key={service.id} value={service.id}>
                              {service.name} ({formatCurrency(service.rate_per_hour)} CHF/Std.)
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung *</label>
                      <input
                        type="text"
                        name="description"
                        value={newItemData.description}
                        onChange={handleItemChange}
                        required
                        placeholder="Beschreibung der Position"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-400"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Menge *</label>
                      <input
                        type="text"
                        name="quantity"
                        value={newItemData.quantity}
                        onChange={handleItemChange}
                        required
                        pattern="[0-9]+(\.[0-9]{1,2})?"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-400"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Einheit *</label>
                      <input
                        type="text"
                        name="unit"
                        value={newItemData.unit}
                        onChange={handleItemChange}
                        required
                        placeholder="Stk., Std., etc."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-400"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Preis pro Einheit (CHF) *</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3flex items-center pointer-events-none">
                             <DollarSign size={16} className="text-gray-400" />
                        </div>
                        <input
                          type="text"
                          name="price_per_unit"
                          value={newItemData.price_per_unit}
                          onChange={handleItemChange}
                          required
                          pattern="[0-9]+(\.[0-9]{1,2})?"
                          className="w-full pl-9 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-400"
                        />
                      </div>
                    </div>

                    <div className="md:col-span-2 flex justify-end">
                      <button
                        type="button"
                        onClick={handleAddItem}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <Check size={18} className="mr-1" /> Position hinzufügen
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* MwSt.-Befreiung Checkbox */}
              <div className="md:col-span-2 mt-4 mb-2 flex items-center">
                <input
                  type="checkbox"
                  id="is_tax_exempt"
                  name="is_tax_exempt"
                  checked={formData.is_tax_exempt}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="is_tax_exempt" className="ml-2 block text-sm text-gray-700">
                  Kunde ist von der MwSt. befreit (Umsatz unter 100'000 CHF/Jahr)
                </label>
              </div>
              
              {/* Rabatt Eingabefeld */}
              <div className="md:col-span-2 mt-4 mb-6">
                <label htmlFor="discount" className="block text-sm font-medium text-gray-700 mb-1">
                  Rabatt (%)
                </label>
                <div className="relative w-full md:w-1/4">
                  <input
                    type="number"
                    id="discount"
                    name="discount"
                    value={formData.discount}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    step="0.1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-400"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500">%</span>
                  </div>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Optionaler Rabatt in Prozent (z.B. 10 für 10%)
                </p>
              </div>

              {/* Liste der Positionen */}
              {offerItems.length === 0 ? (
                <div className="bg-white border border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <p className="text-gray-500">Noch keine Positionen vorhanden. Klicken Sie auf "Position hinzufügen".</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pos.</th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beschreibung</th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Menge</th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Einheit</th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Preis/Einheit</th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                          <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Reihenfolge</th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Aktion</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {offerItems.map((item, index) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              <div className="flex items-center">
                                {item.item_type === 'material' ? (
                                  <Package size={16} className="mr-2 text-green-500" />
                                ) : (
                                  <Wrench size={16} className="mr-2 text-blue-500" />
                                )}
                                <span className="font-medium">{item.description}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(item.quantity)}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{item.unit}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                              {formatCurrency(item.price_per_unit)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                              {formatCurrency(item.total_price || (item.quantity * item.price_per_unit))}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => moveItemUp(index)}
                                  disabled={index === 0}
                                  className={`${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800'} focus:outline-none`}
                                  title="Nach oben"
                                >
                                  <ChevronUp size={18} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveItemDown(index)}
                                  disabled={index === offerItems.length - 1}
                                  className={`${index === offerItems.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800'} focus:outline-none`}
                                  title="Nach unten"
                                >
                                  <ChevronDown size={18} />
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                              <button
                                type="button"
                                onClick={() => handleRemoveItem(item.id)}
                                className="text-red-600 hover:text-red-800 focus:outline-none"
                                title="Löschen"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {/* Zusammenfassung */}
                        <tr className="bg-gray-50">
                          <td colSpan={5} className="px-4 py-3 text-right font-medium text-gray-700">Zwischensumme:</td>
                          <td colSpan={2} className="px-4 py-3 text-right font-medium text-gray-700">
                            CHF {formatCurrency(calculateTotalPrice())}
                          </td>
                          <td></td>
                        </tr>
                        
                        {parseFloat(formData.discount) > 0 && (
                          <tr className="bg-gray-50">
                            <td colSpan={5} className="px-4 py-3 text-right font-medium text-gray-700">Rabatt ({formData.discount}%):</td>
                            <td colSpan={2} className="px-4 py-3 text-right font-medium text-red-600">
                              - CHF {formatCurrency(calculateDiscountAmount())}
                            </td>
                            <td></td>
                          </tr>
                        )}
                        
                        {parseFloat(formData.discount) > 0 && (
                          <tr className="bg-gray-50">
                            <td colSpan={5} className="px-4 py-3 text-right font-medium text-gray-700">Nach Rabatt:</td>
                            <td colSpan={2} className="px-4 py-3 text-right font-medium text-gray-700">
                              CHF {formatCurrency(calculateDiscountedPrice())}
                            </td>
                            <td></td>
                          </tr>
                        )}
                        
                        {!formData.is_tax_exempt ? (
                          <tr className="bg-gray-50">
                            <td colSpan={5} className="px-4 py-3 text-right font-medium text-gray-700">MwSt. (8.1%):</td>
                            <td colSpan={2} className="px-4 py-3 text-right font-medium text-gray-700">
                              CHF {formatCurrency(calculateTaxAmount())}
                            </td>
                            <td></td>
                          </tr>
                        ) : (
                          <tr className="bg-gray-50">
                            <td colSpan={5} className="px-4 py-3 text-right font-medium text-gray-700">MwSt.:</td>
                            <td colSpan={2} className="px-4 py-3 text-right font-medium text-gray-700">
                              MwSt.-befreit
                            </td>
                            <td></td>
                          </tr>
                        )}
                        
                        <tr className="bg-gray-100">
                          <td colSpan={5} className="px-4 py-3 text-right font-semibold text-gray-800">Gesamtbetrag:</td>
                          <td colSpan={2} className="px-4 py-3 text-right font-semibold text-gray-800 text-lg">
                            CHF {formatCurrency(calculateTotalWithTax())}
                          </td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-6 border-t border-gray-200">
              <button
                type="submit"
                className="btn-modern inline-flex items-center px-8 py-3.5 text-base font-semibold text-white bg-primary-600 hover:bg-primary-700 shadow-md-soft focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {currentOffer ? (
                  <>
                    <CheckCircle size={20} className="mr-2" /> Offerte aktualisieren
                  </>
                ) : (
                  <>
                    <PlusCircle size={20} className="mr-2" /> Offerte erstellen
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filter- und Suchleiste */}
      <div className="mb-6 flex flex-col md:flex-row items-stretch md:items-center gap-4">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={18} className="text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Offerten durchsuchen..."
            className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-400 shadow-sm"
          />
        </div>
        
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Filter size={18} className="text-gray-400" />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-400 shadow-sm appearance-none"
          >
            <option value="all">Alle Status</option>
            <option value="draft">Entwurf</option>
            <option value="sent">Gesendet</option>
            <option value="accepted">Akzeptiert</option>
            <option value="rejected">Abgelehnt</option>
          </select>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <ArrowUpDown size={16} className="text-gray-400" />
          </div>
        </div>
      </div>

      {/* Offertenliste */}
      <div className="card-modern overflow-hidden">
        <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Offertenliste</h2>
          <div className="text-xs sm:text-sm text-gray-600 font-medium">
            {filteredOffers.length} {filteredOffers.length === 1 ? 'Offerte' : 'Offerten'}
          </div>
        </div>
        
        {filteredOffers.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">Keine Offerten vorhanden</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titel</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kunde</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Betrag</th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Gültig bis</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Erstellt am</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOffers.map((offer) => (
                  <tr key={offer.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{offer.title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Users size={16} className="text-gray-400 mr-2" />
                        <div className="text-sm text-gray-900">{offer.company_name || `${offer.first_name} ${offer.last_name}`}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                      CHF {formatCurrency(offer.total_amount)}
                      {offer.is_tax_exempt && (
                        <span className="ml-1 text-xs bg-orange-100 text-orange-800 py-0.5 px-1.5 rounded-full">
                          MwSt.-befreit
                        </span>
                      )}
                      {parseFloat(offer.discount) > 0 && (
                        <span className="ml-1 text-xs bg-green-100 text-green-800 py-0.5 px-1.5 rounded-full">
                          {offer.discount}% Rabatt
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="relative" data-dropdown={offer.id}>
                        <button 
                          onClick={(e) => toggleStatusDropdown(offer.id, e)}
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(offer.status)} border cursor-pointer`}
                        >
                          {getStatusIcon(offer.status)}
                          {getStatusText(offer.status)}
                        </button>
                        
                        {/* Dropdown für Statusänderung */}
                        {statusDropdownId === offer.id && (
                          <div className="absolute z-10 left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                            <div className="py-1" role="menu" aria-orientation="vertical">
                              <button
                                onClick={() => handleStatusChange(offer.id, 'draft')}
                                className={`w-full text-left px-4 py-2 text-sm ${offer.status === 'draft' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-100'}`}
                                role="menuitem"
                              >
                                <Edit size={16} className="inline mr-2 text-yellow-600" />
                                Entwurf
                              </button>
                              <button
                                onClick={() => handleStatusChange(offer.id, 'sent')}
                                className={`w-full text-left px-4 py-2 text-sm ${offer.status === 'sent' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-100'}`}
                                role="menuitem"
                              >
                                <Send size={16} className="inline mr-2 text-blue-600" />
                                Gesendet
                              </button>
                              <button
                                onClick={() => handleStatusChange(offer.id, 'accepted')}
                                className={`w-full text-left px-4 py-2 text-sm ${offer.status === 'accepted' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-100'}`}
                                role="menuitem"
                              >
                                <CheckCircle size={16} className="inline mr-2 text-green-600" />
                                Akzeptiert
                              </button>
                              <button
                                onClick={() => handleStatusChange(offer.id, 'rejected')}
                                className={`w-full text-left px-4 py-2 text-sm ${offer.status === 'rejected' ? 'bg-gray-100 font-medium' : 'hover:bg-gray-100'}`}
                                role="menuitem"
                              >
                                <AlertCircle size={16} className="inline mr-2 text-red-600" />
                                Abgelehnt
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                      <div className="flex items-center justify-center">
                        <Calendar size={16} className="text-gray-400 mr-2" />
                        {formatDate(offer.valid_until)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                      <div className="flex items-center justify-end">
                        <Clock size={16} className="text-gray-400 mr-2" />
                        {formatDate(offer.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-3">
                        {/* PDF-Vorschau anzeigen */}
                        <button 
                          onClick={() => handlePreviewPDF(offer.id)} 
                          className="touch-target p-2 rounded-lg text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-all"
                          title="PDF-Vorschau"
                        >
                          <Eye size={20} />
                        </button>
                        
                        {/* PDF herunterladen */}
                        <button 
                          onClick={() => handleDownloadPDF(offer.id, offer.title)} 
                          className="touch-target p-2 rounded-lg text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-all"
                          title="PDF herunterladen"
                        >
                          <Download size={20} />
                        </button>
                        
                        {(offer.status === 'draft' || offer.status === 'rejected') && (
                          <>
                            <button 
                              onClick={() => handleEditOffer(offer.id)} 
                              className="touch-target p-2 rounded-lg text-amber-600 hover:bg-amber-50 hover:text-amber-700 transition-all"
                              title="Bearbeiten"
                            >
                              <Edit size={20} />
                            </button>
                            
                            {/* PDF per E-Mail versenden */}
                            <button 
                              onClick={() => handleSendPDFByEmail(offer.id)} 
                              className="touch-target p-2 rounded-lg text-green-600 hover:bg-green-50 hover:text-green-700 transition-all"
                              title="Per E-Mail senden"
                            >
                              <Mail size={20} />
                            </button>
                            
                            {/* Offerte löschen */}
                            <button 
                              onClick={() => handleDeleteOffer(offer.id, offer.title)} 
                              className="touch-target p-2 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition-all"
                              title="Löschen"
                            >
                              <Trash2 size={20} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OffersPage;
					  
